import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { GetPlayerListTableData } from '@/modules/manage-player/type'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { Textarea } from '@/common/ui/textarea'
import useMutationUpdatePlayer from '@/modules/manage-player/api/hooks/useMutationUpdatePlayer'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import { Lock, UnlockKeyhole } from 'lucide-react'
import { handlePlayerFormError } from '@/modules/manage-player/utils/handlePlayerFormError'

const formSchema = z.object({
	banReason: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface PlayerBanModalProps {
	player: GetPlayerListTableData
}

export default function PlayerBanModal({ player }: PlayerBanModalProps) {
	const [open, setOpen] = useState(false)
	const [unbanConfirmOpen, setUnbanConfirmOpen] = useState(false)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			banReason: '',
		},
	})

	const { mutate: updatePlayer } = useMutationUpdatePlayer()
	const queryClient = useQueryClient()

	const handleUnban = () => {
		try {
			updatePlayer(
				{
					id: player.id,
					payload: {
						isBanned: false,
						banReason: '',
					},
				},
				{
					onSuccess: () => {
						toast.success('ยกเลิกการแบนผู้เล่นเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						setUnbanConfirmOpen(false)
					},
					onError: (error) => handlePlayerFormError(error, form, 'edit'),
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const onSubmit = (values: FormValues) => {
		try {
			updatePlayer(
				{
					id: player.id,
					payload: {
						isBanned: true,
						banReason: values.banReason,
					},
				},
				{
					onSuccess: () => {
						toast.success('แบนผู้เล่นเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						form.reset()
						setOpen(false)
					},
					onError: (error) => handlePlayerFormError(error, form, 'edit'),
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	if (player.isBanned) {
		return (
			<>
				<Button
					variant="ghost"
					size="icon"
					onClick={() => setUnbanConfirmOpen(true)}
					title="ยกเลิกการแบน"
				>
					<Lock className="h-4 w-4" />
				</Button>

				<Dialog open={unbanConfirmOpen} onOpenChange={setUnbanConfirmOpen}>
					<DialogContent className="sm:max-w-[425px]">
						<DialogHeader>
							<DialogTitle>ยืนยันการยกเลิกการแบน ({player.phone})</DialogTitle>
						</DialogHeader>

						<div className="pt-4">
							<p>คุณต้องการยกเลิกการแบนผู้เล่นนี้ใช่หรือไม่?</p>
						</div>

						<div className="flex justify-end gap-4 pt-4">
							<Button
								variant="outline"
								onClick={() => setUnbanConfirmOpen(false)}
								type="button"
							>
								ยกเลิก
							</Button>
							<Button onClick={handleUnban} variant="destructive">
								ยืนยัน
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</>
		)
	}

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				onClick={() => setOpen(true)}
				title="แบนผู้เล่น"
			>
				<UnlockKeyhole className="h-4 w-4" />
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>แบนผู้เล่น ({player.phone})</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="banReason"
								render={({ field }) => (
									<FormItem>
										<FormLabel>เหตุผลที่แบน</FormLabel>
										<FormControl>
											<Textarea
												placeholder="กรุณากรอกเหตุผลที่แบน"
												className="resize-none"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-4">
								<Button
									variant="outline"
									onClick={() => setOpen(false)}
									type="button"
								>
									ยกเลิก
								</Button>
								<Button type="submit" variant="destructive">
									แบนผู้เล่น
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
